import type { Papel } from '@core/types';
import {
  useCallback, useEffect, useRef, useState, useSyncExternalStore, type ReactNode, type RefObject,
} from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router';
import { hashKey, useQueryClient } from '@tanstack/react-query';
import Marca from './Marca';
import { EVENTO_ATUALIZACAO } from '../lib/atualizacao';
import { hojeISO } from '../lib/formato';
import { CHAVES_DO_SINO } from '../lib/notificacoes';
import Sino from './Sino';
import {
  useSessao, podeGerenciarCatalogo, podeVerCaixa, podeGerenciarEquipe, podeVerFinancas,
  podeVender,
  podeGerenciarVendas,
  podeVerFaturamento,
  podeAdministrar,
  podeConferirCadastro,
  podeLancarCompraNaHora,
  useMeuDinheiroLiberado,
} from '../lib/sessao';

// --- Conexão: o balcão precisa saber que está olhando FOTO, não filme --------
//
// Com o service worker no ar, ficar sem internet deixou de ser tela branca: a
// busca responde com a última cópia guardada no aparelho. Isso é ótimo e é
// perigoso pelo mesmo motivo — o vendedor pode prometer uma peça que saiu do
// estoque há duas horas. Daí o aviso fixo com a HORA do dado, que não é
// enfeite: é o que evita vender peça que já foi.

/**
 * Telas cujo propósito é ESCREVER no banco — onde o vendedor digita um
 * atendimento inteiro antes de apertar o botão. Sem internet elas são bloqueadas
 * com recado, em vez de deixar a pessoa preencher tudo para falhar no fim.
 *
 * Fora da lista de propósito: `/`, `/peca/:id`, `/vendas`, `/orcamentos` —
 * consultar continua liberado, que é justamente o que o cache offline serve.
 * `/caixa`, `/a-ver`, `/equipe`, `/painel` e `/metas` também ficam de fora:
 * são telas de leitura com um formulário no canto, e ali a escrita sem rede já
 * falha na hora com o recado do service worker.
 */
const ROTAS_DE_ESCRITA = [
  /^\/venda$/,
  /^\/orcar$/,
  /^\/entrada$/,
  /^\/cadastro$/,
  /^\/cadastro\/balcao$/,
  /^\/importar$/,
  /^\/peca\/[^/]+\/editar$/,
  // "Paguei fornecedor" (095): a tela EXISTE para gravar — sem rede, o
  // fornecedor, o valor e a forma escolhidos morreriam no botão.
  /^\/compra-na-hora$/,
];

export function escreveNoBanco(caminho: string): boolean {
  const limpo = caminho.replace(/\/+$/, '') || '/';
  return ROTAS_DE_ESCRITA.some((rota) => rota.test(limpo));
}

/** De quando é o dado que está na tela, em português de balcão. */
export function textoDoDado(ultimoSync: number | null, agora: number): string {
  if (ultimoSync === null || !Number.isFinite(ultimoSync) || ultimoSync > agora) {
    return 'não dá para saber de quando são estes dados';
  }
  const minutos = Math.floor((agora - ultimoSync) / 60_000);
  if (minutos < 1) return 'dados de menos de 1 minuto atrás';
  if (minutos < 60) return `dados de ${minutos} min atrás`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `dados de ${horas} h atrás`;
  const dias = Math.floor(horas / 24);
  return `dados de ${dias} ${dias > 1 ? 'dias' : 'dia'} atrás`;
}

/**
 * O MENU DO SISTEMA, por papel — DADO, não JSX (17/08/2026; redesenhado no
 * Grafite em 29/10/2026).
 *
 * Até 28/10 a navegação morava em DOIS lugares: uma barra de baixo com até 11
 * itens que rolavam para o lado, e um menu do usuário (o nome, no canto) com o
 * resto em grupos. Achar uma tela exigia lembrar em qual dos dois ela estava.
 * Agora é UMA lista agrupada por assunto, a mesma nos dois tamanhos de tela:
 *
 *   computador (≥ 1024 px)  menu lateral, sempre aberto;
 *   celular                 barra de baixo com 4 atalhos + "Mais", que abre
 *                           esta mesma lista.
 *
 * AS TRAVAS DE PAPEL são as mesmas de antes, uma a uma — só mudaram de lugar.
 * Cada item usa a MESMA função `pode*()` que a rota usa em App.tsx (Protegida),
 * e Layout.test.ts confere isso lendo o App.tsx: quem não pode abrir a tela não
 * vê o item, e toda tela que o papel abre está no menu dele. Esconder é
 * conveniência; quem nega de verdade é a RLS.
 *
 *   vendedor  Balcão · Estoque (pedidos do dia e mapa)
 *   gerente   Balcão · Estoque · Dinheiro (vale, fiado, relatórios) · Gestão
 *   dono      tudo, com "Dinheiro · dono"
 */
export interface ItemDoMenu {
  para: string;
  rotulo: string;
  /** O número ao lado do item — sempre um dado que o SINO já busca. */
  contador?: ContadorDoMenu;
}

export type ContadorDoMenu = 'encomendas-hoje' | 'contas-ate-hoje';

export interface GrupoDoMenu {
  titulo: string;
  itens: ItemDoMenu[];
}

/** O que o menu precisa saber além do papel. Ausente = como sempre foi. */
export interface OpcoesDoMenu {
  /** O papel desta pessoa está liberado no Meu dinheiro (migração 079)? */
  meuDinheiro?: boolean;
}

type LinhaDoMenu = ItemDoMenu & { visivel: boolean };

export function gruposDoMenu(papel: Papel | null, opcoes?: OpcoesDoMenu): GrupoDoMenu[] {
  const grupos: Array<{ titulo: string; itens: LinhaDoMenu[] }> = [
    {
      // Meu dinheiro (ADR-008): o planejamento de CADA UM, que ninguém da loja
      // vê. No topo, separado do trabalho, e só quando o dono liberou o papel
      // (o botão da tela de Equipe). Quem nega de verdade é o banco pessoal.
      titulo: 'Pessoal',
      itens: [
        { para: '/pessoal/meu-dinheiro', rotulo: 'Meu dinheiro', visivel: podeVender(papel) && Boolean(opcoes?.meuDinheiro) },
      ],
    },
    {
      // O atendimento, na ordem de frequência do balcão. podeVender = qualquer
      // perfil ATIVO: orçamento é o documento diário; promoção é a pauta de
      // upsell; todo vendedor encomenda (a RLS recorta); a agenda é de todos.
      // Sem papel (sessão caindo), nem o Balcão aparece.
      titulo: 'Balcão',
      itens: [
        { para: '/', rotulo: 'Buscar peça', visivel: podeVender(papel) },
        { para: '/venda', rotulo: 'Vender', visivel: podeVender(papel) },
        { para: '/orcar', rotulo: 'Orçar', visivel: podeVender(papel) },
        // NOTAS (072), o mural: um mural que ninguém vê não é mural — por isso
        // ele é o quarto atalho da barra do celular (ver itensDaBarra).
        { para: '/notas', rotulo: 'Notas', visivel: podeVender(papel) },
        // Uma rota, dois escopos: a RLS decide se aparecem as vendas de todos
        // ou só as de quem está logado.
        { para: '/vendas', rotulo: 'Vendas', visivel: podeVender(papel) },
        // Encomendas: dono/gerente veem TODAS; vendedor, as dele — quem
        // recorta é a RLS, a tela é a mesma.
        { para: '/encomendas', rotulo: 'Encomendas', contador: 'encomendas-hoje', visivel: podeVender(papel) },
        // Clientes segue a trava da ROTA (podeGerenciarVendas): o vendedor
        // continua cadastrando cliente de dentro da Venda.
        { para: '/clientes', rotulo: 'Clientes', visivel: podeGerenciarVendas(papel) },
        { para: '/orcamentos', rotulo: 'Orçamentos', visivel: podeVender(papel) },
        { para: '/promocoes', rotulo: 'Promoções', visivel: podeVender(papel) },
        { para: '/agenda', rotulo: 'Agenda', visivel: podeVender(papel) },
        // Os pedidos do portal B2B (062): o gerente entra pelos LOGINS e pelo
        // desconto do parceiro, que a RLS §17 continua lhe dando; a aba de
        // pedidos, que tem o total, é a que ele não vê lá dentro.
        { para: '/parceiros', rotulo: 'Parceiros (pedidos B2B)', visivel: podeGerenciarVendas(papel) },
        // A fila de conferência (093), do lado de quem CADASTRA: as peças que
        // ele pôs no sistema, liberadas com a rasura ou recusadas com o motivo.
        // Não há item "cadastrar": a porta é o vazio da busca, que obriga a
        // buscar antes e evita a peça em dobro.
        { para: '/conferencia', rotulo: 'Minhas peças cadastradas', visivel: podeVender(papel) && !podeConferirCadastro(papel) },
      ],
    },
    {
      titulo: 'Estoque',
      itens: [
        // O fechamento (052), aberto a todos desde 053: o vendedor entra para
        // SUGERIR peça; a tela mostra a ele só a parte que é dele.
        { para: '/pedidos', rotulo: 'Pedidos do dia (fechamento)', visivel: podeVender(papel) },
        { para: '/entrada', rotulo: 'Entrada de mercadoria', visivel: podeGerenciarCatalogo(papel) },
        // A compra paga NA HORA (095): gerente e dono lançam; o mês dentro da
        // tela é só do dono.
        { para: '/compra-na-hora', rotulo: 'Paguei fornecedor (na hora)', visivel: podeLancarCompraNaHora(papel) },
        { para: '/compras', rotulo: 'Pedidos ao fornecedor', visivel: podeGerenciarCatalogo(papel) },
        { para: '/cadastro', rotulo: 'Nova peça', visivel: podeGerenciarCatalogo(papel) },
        // A lista de faxina (120). O rótulo é a PERGUNTA de quem abre o menu
        // querendo cadastrar — "o que falta?" —, não a palavra "faxina", que
        // só diz algo para quem leu o plano.
        { para: '/faxina', rotulo: 'O que falta cadastrar', visivel: podeGerenciarCatalogo(papel) },
        // O lado de quem LIBERA. O rótulo guarda "(conferência)" de propósito:
        // é a palavra da frase com que registrar_venda recusa a peça pendente
        // ("na tela de Conferência"), e assim a frase não precisa mudar.
        { para: '/conferencia', rotulo: 'Peças para liberar (conferência)', visivel: podeConferirCadastro(papel) },
        { para: '/importar', rotulo: 'Importar CSV', visivel: podeGerenciarCatalogo(papel) },
        // O mapa 3D (25/09/2026): onde está cada peça. Achar é de todos;
        // mudar a zona é da gerência — quem recorta é a RLS de stock.
        { para: '/mapa', rotulo: 'Mapa da loja', visivel: podeVender(papel) },
        { para: '/fornecedores', rotulo: 'Fornecedores', visivel: podeGerenciarCatalogo(papel) },
        { para: '/caracteristicas', rotulo: 'Características das peças', visivel: podeAdministrar(papel) },
        { para: '/estoque', rotulo: 'Estoque (conferência)', visivel: podeAdministrar(papel) },
      ],
    },
    {
      // "Dinheiro · dono" para o dono; para o gerente, só "Dinheiro", com o que
      // a régua lhe deixa (vale, fiado e a reposição dos relatórios). O
      // vendedor não vê o grupo.
      titulo: podeVerFinancas(papel) ? 'Dinheiro · dono' : 'Dinheiro',
      itens: [
        // O caixa é só do dono desde 28/09/2026: contar o dinheiro do dia é
        // ver o faturamento do dia.
        { para: '/caixa', rotulo: 'Caixa', visivel: podeVerCaixa(papel) },
        { para: '/painel', rotulo: 'Painel', visivel: podeVerFinancas(papel) },
        { para: '/contas', rotulo: 'Contas a pagar', contador: 'contas-ate-hoje', visivel: podeVerFinancas(papel) },
        { para: '/fluxo', rotulo: 'Fluxo de caixa', visivel: podeVerFinancas(papel) },
        { para: '/provisoes', rotulo: 'Provisões (simulador)', visivel: podeVerFinancas(papel) },
        // Meta é faturamento com nome de alvo: só dono desde 28/09/2026.
        { para: '/metas', rotulo: 'Metas do trimestre', visivel: podeVerFaturamento(papel) },
        // Vale e fiado são a mesma régua (gestão de venda): o gerente dá o
        // crédito e cobra a dívida — 83 recebimentos em 90 dias.
        { para: '/a-ver', rotulo: 'Notas "a ver"', visivel: podeGerenciarVendas(papel) },
        { para: '/fiado', rotulo: 'Fiado (quem está devendo)', visivel: podeGerenciarVendas(papel) },
        // Relatórios continua com o gerente por causa da REPOSIÇÃO (o que
        // saiu e falta comprar, em unidades). O faturamento de dentro da tela
        // é escondido lá, bloco a bloco — ver Relatorios.tsx.
        { para: '/relatorios', rotulo: 'Relatórios', visivel: podeGerenciarCatalogo(papel) },
      ],
    },
    {
      titulo: 'Gestão',
      itens: [
        { para: '/equipe', rotulo: 'Equipe', visivel: podeGerenciarEquipe(papel) },
        // A fila de impressão é de gerência: quem imprime o vale é quem
        // precisa reimprimir quando o papel não sai.
        { para: '/impressoes', rotulo: 'Impressões (fila)', visivel: podeGerenciarVendas(papel) },
        { para: '/sistema', rotulo: 'Sistema', visivel: podeAdministrar(papel) },
      ],
    },
  ];

  return grupos
    .map((g) => ({
      titulo: g.titulo,
      itens: g.itens.filter((i) => i.visivel)
        .map(({ para, rotulo, contador }) => (contador ? { para, rotulo, contador } : { para, rotulo })),
    }))
    .filter((g) => g.itens.length > 0);
}

/** m:ss que falta para o logout automático. */
export function contagemRegressiva(expiraEm: number | null, agora: number): string {
  const segundos = Math.max(0, Math.ceil(((expiraEm ?? agora) - agora) / 1000));
  return `${Math.floor(segundos / 60)}:${String(segundos % 60).padStart(2, '0')}`;
}

/** Pergunta ao service worker quando foi a última conversa com o servidor. */
function perguntarUltimoSync(): Promise<number | null> {
  return new Promise((resolve) => {
    const sw = typeof navigator === 'undefined' ? undefined : navigator.serviceWorker;
    if (!sw?.controller || typeof MessageChannel === 'undefined') {
      resolve(null);
      return;
    }
    const canal = new MessageChannel();
    // Sem resposta rápida (SW dormindo, versão antiga sem esta mensagem) o
    // aviso sai sem o horário — melhor do que travar esperando.
    const desistir = window.setTimeout(() => resolve(null), 700);
    canal.port1.onmessage = (evento) => {
      window.clearTimeout(desistir);
      const em = (evento.data as { em?: unknown } | null)?.em;
      resolve(typeof em === 'number' ? em : null);
    };
    sw.controller.postMessage({ tipo: 'serginho:ultimo-sync' }, [canal.port2]);
  });
}

function useConexao(): boolean {
  const [online, setOnline] = useState(
    () => (typeof navigator === 'undefined' ? true : navigator.onLine !== false),
  );
  useEffect(() => {
    const subiu = () => setOnline(true);
    const caiu = () => setOnline(false);
    window.addEventListener('online', subiu);
    window.addEventListener('offline', caiu);
    return () => {
      window.removeEventListener('online', subiu);
      window.removeEventListener('offline', caiu);
    };
  }, []);
  return online;
}

// Navegação por barra inferior no celular: no balcão o app é usado em pé, com
// uma mão só. Menu no topo obrigaria a esticar o polegar até fora do alcance.
const Icone = ({ d }: { d: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
       strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
    <path d={d} />
  </svg>
);

const ICONES = {
  busca: 'M11 3a8 8 0 1 0 0 16 8 8 0 0 0 0-16zm10 18l-4.35-4.35',
  venda: 'M3 3h2l2.4 12.3a2 2 0 0 0 2 1.7h7.7a2 2 0 0 0 2-1.6L21 8H6M9 21h.01M18 21h.01',
  vendas: 'M4 4v16h16M8 16V9m4 7V5m4 11v-4',
  entrada: 'M12 3v12m0 0l-4-4m4 4l4-4M4 21h16',
  caixa: 'M3 7h18v12H3zM3 7l2-4h14l2 4M12 12v4M9 12h6',
  painel: 'M3 12a9 9 0 0 1 18 0M12 12l4-3M12 21h.01',
  // Folha com linhas: o documento do orçamento.
  orcamento: 'M6 2h9l5 5v15H6zM15 2v5h5M9 13h6M9 17h4',
  // Caixa fechada com fita: a encomenda esperando o cliente.
  encomenda: 'M21 8l-9-5-9 5v8l9 5 9-5zM3 8l9 5 9-5M12 13v8',
  // Pessoa: o cadastro de clientes.
  cliente: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
  // Prancheta com visto: o fechamento do dia (o que pedir).
  pedidos: 'M9 4h6v3H9zM7 4H5v17h14V4h-2M9 13l2 2 4-4',
  // Papel com a ponta dobrada: o recado pendurado no mural (072). A dobra é o
  // que o separa do orçamento, que também é uma folha — de longe, num ícone de
  // 20px, a silhueta é a única diferença que se enxerga.
  notas: 'M5 4h14v11l-4 5H5zM19 15h-4v5M9 9h6M9 13h4',
  // Três linhas: o resto do menu.
  mais: 'M4 6h16M4 12h16M4 18h16',
};

/**
 * A ORDEM DE USO do balcão — a régua da barra de baixo desde 17/08/2026, e o
 * rótulo curto e o ícone de cada atalho. É por frequência, não por
 * importância: Buscar, Vender e Orçar são o atendimento; Notas veio em quarto
 * de propósito (072), porque um mural que ninguém vê não é mural.
 */
const ORDEM_DA_BARRA: ReadonlyArray<{ para: string; curto: string; icone: string }> = [
  { para: '/', curto: 'Buscar', icone: ICONES.busca },
  { para: '/venda', curto: 'Vender', icone: ICONES.venda },
  { para: '/orcar', curto: 'Orçar', icone: ICONES.orcamento },
  { para: '/notas', curto: 'Notas', icone: ICONES.notas },
  { para: '/vendas', curto: 'Vendas', icone: ICONES.vendas },
  { para: '/encomendas', curto: 'Encomendas', icone: ICONES.encomenda },
  { para: '/clientes', curto: 'Clientes', icone: ICONES.cliente },
  { para: '/pedidos', curto: 'Pedidos', icone: ICONES.pedidos },
  { para: '/entrada', curto: 'Entrada', icone: ICONES.entrada },
  { para: '/caixa', curto: 'Caixa', icone: ICONES.caixa },
  { para: '/painel', curto: 'Painel', icone: ICONES.painel },
];

/** Quantos atalhos a barra do celular mostra, além do "Mais". */
export const ATALHOS_NA_BARRA = 4;

/**
 * A BARRA DE BAIXO do celular, por papel: os 4 primeiros da ordem de uso QUE O
 * PAPEL PODE ABRIR, e um "Mais" com o menu inteiro (29/10/2026).
 *
 * Até 28/10 a barra tinha até 11 itens e rolava para o lado: a 360 px só os
 * quatro primeiros cabiam sem arrastar, e o resto ficava escondido à direita.
 * Agora são esses quatro, fixos, com alvo de 44 px — e o resto está a um toque
 * no "Mais", agrupado, em vez de a um arraste sem rótulo.
 *
 * A visibilidade vem de gruposDoMenu: a barra nunca mostra o que o menu
 * esconderia. Pela ordem de uso de hoje os três papéis têm os mesmos quatro
 * atalhos (Buscar, Vender, Orçar, Notas) — as telas de gestão do dono e do
 * gerente vinham depois deles na barra antiga também.
 */
export function itensDaBarra(
  papel: Papel | null,
): Array<{ para: string; rotulo: string; icone: string }> {
  const pode = new Set(gruposDoMenu(papel).flatMap((g) => g.itens.map((i) => i.para)));
  return ORDEM_DA_BARRA
    .filter((i) => pode.has(i.para))
    .slice(0, ATALHOS_NA_BARRA)
    .map(({ para, curto, icone }) => ({ para, rotulo: curto, icone }));
}

/** O caminho atual está NESTE item? "/" só no início; os outros, com filhos. */
export function estaNoItem(caminho: string, para: string): boolean {
  const limpo = caminho.replace(/\/+$/, '') || '/';
  if (para === '/') return limpo === '/';
  return limpo === para || limpo.startsWith(`${para}/`);
}

/**
 * Lê, sem perguntar nada ao banco, o que o sino já trouxe. Não é useQuery de
 * propósito: um segundo observador com `refetchInterval` faria a mesma
 * consulta duas vezes a cada 10 min — e o combinado é contador só com dado
 * que já existe, sem consulta nova.
 *
 * Só acorda com o `updated` DESTA chave: o cache também avisa quando uma
 * consulta qualquer NASCE, e isso acontece no meio do desenho de outra tela —
 * acordar ali é o "Cannot update a component while rendering a different
 * component" do React (visto na captura da Venda, 29/10/2026).
 */
function useDadoJaBuscado(chave: readonly unknown[]): unknown {
  const cliente = useQueryClient();
  const hash = hashKey(chave);
  const assinar = useCallback(
    (avisar: () => void) => cliente.getQueryCache().subscribe((evento) => {
      if (evento.type === 'updated' && evento.query.queryHash === hash) avisar();
    }),
    [cliente, hash],
  );
  const ler = () => cliente.getQueryData(chave);
  return useSyncExternalStore(assinar, ler, ler);
}

const quantos = (dado: unknown): number | undefined =>
  (Array.isArray(dado) && dado.length > 0 ? dado.length : undefined);

function useContadoresDoMenu(): Partial<Record<ContadorDoMenu, number>> {
  const hoje = hojeISO();
  const encomendas = useDadoJaBuscado(CHAVES_DO_SINO.encomendas(hoje));
  const contas = useDadoJaBuscado(CHAVES_DO_SINO.contas(hoje));
  return { 'encomendas-hoje': quantos(encomendas), 'contas-ate-hoje': quantos(contas) };
}

/** Cor (sinal) e frase para leitor de tela de cada contador. */
const CONTADORES: Record<ContadorDoMenu, { cor: string; frase: string }> = {
  'encomendas-hoje': { cor: 'text-alerta-claro', frase: 'vencendo hoje' },
  'contas-ate-hoje': { cor: 'text-erro-claro', frase: 'vencendo até hoje' },
};

/** A lista agrupada — a mesma no menu lateral e no "Mais" do celular. */
function ListaDoMenu({ grupos, grande, aoEscolher }: {
  grupos: GrupoDoMenu[];
  /** No celular: alvo de 44 px e letra maior. */
  grande?: boolean;
  aoEscolher?: () => void;
}) {
  const contadores = useContadoresDoMenu();
  return (
    <div className={grande ? 'space-y-4' : 'space-y-5'}>
      {grupos.map((grupo) => (
        <div key={grupo.titulo}>
          <p className="px-2 pb-1.5 text-[0.6875rem] font-medium uppercase tracking-[0.08em] text-dim">
            {grupo.titulo}
          </p>
          <ul className="space-y-0.5">
            {grupo.itens.map((item) => {
              const n = item.contador ? contadores[item.contador] : undefined;
              return (
                <li key={item.para}>
                  <NavLink
                    to={item.para}
                    end={item.para === '/'}
                    onClick={aoEscolher}
                    className={({ isActive }) => [
                      'flex items-center justify-between gap-3 rounded-lg px-2 leading-snug transition-[color,background-color]',
                      grande ? 'min-h-11 py-2 text-[0.9375rem]' : 'py-1.5 text-sm',
                      isActive ? 'bg-panel-alt text-paper' : 'text-mute hover:bg-panel hover:text-paper',
                    ].join(' ')}
                  >
                    {/* Quebra em vez de cortar: "Peças para liberar (conferência)"
                        não cabe em 240 px, e rótulo cortado é rótulo adivinhado. */}
                    <span className="min-w-0">{item.rotulo}</span>
                    {item.contador && n !== undefined && (
                      <span className={`numero shrink-0 text-xs ${CONTADORES[item.contador].cor}`}>
                        {n}<span className="sr-only"> {CONTADORES[item.contador].frase}</span>
                      </span>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

/** Quem está logado, e a saída — no pé do menu lateral e no topo do "Mais". */
function useSair() {
  const { sair } = useSessao();
  const navigate = useNavigate();
  return async () => { await sair(); navigate('/login'); };
}

/** O MENU LATERAL do computador (≥ 1024 px), como no desenho aprovado. */
function MenuLateral({ grupos }: { grupos: GrupoDoMenu[] }) {
  const { perfil, papel } = useSessao();
  const sair = useSair();
  return (
    <nav
      aria-label="Menu do sistema"
      className="hidden border-r border-fio bg-ink print:hidden lg:sticky lg:top-0 lg:flex lg:h-svh lg:w-60 lg:shrink-0 lg:flex-col"
    >
      <Link to="/" aria-label="Início" className="flex items-center gap-2.5 px-5 pb-5 pt-5">
        <Marca className="h-5 w-auto shrink-0 text-marca" />
        <span className="flex flex-col leading-tight">
          <span className="text-sm font-semibold">Serginho</span>
          <span className="text-xs text-dim">Sistema da loja</span>
        </span>
      </Link>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-5">
        <ListaDoMenu grupos={grupos} />
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-fio px-5 py-3 text-[0.8125rem]">
        <span className="min-w-0 truncate text-dim">
          <span className="text-mute">{perfil?.nome ?? '—'}</span>
          {papel && <> · {papel}</>}
        </span>
        <button type="button" onClick={() => { void sair(); }}
                className="shrink-0 rounded-md px-2 py-1 text-[0.8125rem] text-mute hover:bg-panel hover:text-paper">
          Sair
        </button>
      </div>
    </nav>
  );
}

/**
 * A página e a folha do "Mais", lado a lado — e o resto da página INERTE
 * enquanto a folha está aberta (revisão do #355, 29/10/2026).
 *
 * Sem isto o Tab saía da folha e passeava pela tela de trás, escondida sob o
 * fundo escuro: o leitor de tela anunciava botões que ninguém via. `inert`
 * tira tudo de trás do foco, do clique e da árvore de acessibilidade de uma
 * vez, e por isso a folha mora FORA do bloco inerte, nunca dentro dele.
 */
export function PaginaComFolha({ aberta, pagina, folha }: {
  aberta: boolean;
  pagina: ReactNode;
  folha: ReactNode;
}) {
  return (
    <>
      <div inert={aberta}>{pagina}</div>
      {aberta && folha}
    </>
  );
}

/** A largura em que o menu lateral entra e a folha do "Mais" some (o `lg`). */
export const MIDIA_TELA_LARGA = '(min-width: 64rem)';

/** O pedaço do MediaQueryList que a vigia usa — e que um teste consegue imitar. */
export interface MidiaTelaLarga {
  matches: boolean;
  addEventListener?(tipo: 'change', ouvinte: (e: { matches: boolean }) => void): void;
  removeEventListener?(tipo: 'change', ouvinte: (e: { matches: boolean }) => void): void;
  /** O jeito antigo (Safari antes do 14). */
  addListener?(ouvinte: (e: { matches: boolean }) => void): void;
  removeListener?(ouvinte: (e: { matches: boolean }) => void): void;
}

/**
 * Fecha o "Mais" quando a tela ALARGA para o menu lateral (revisão do #355).
 *
 * A folha é `lg:hidden`: com ela aberta, um iPad que gira ou uma janela que
 * alarga passa dos 1024 px, a folha some — e a página ficava `inert` e sem
 * rolagem, sem nenhum toque que a destravasse. Devolve a função que tira o
 * ouvinte (o desmontar do efeito).
 */
export function vigiarTelaLarga(midia: MidiaTelaLarga, aoAlargar: () => void): () => void {
  if (midia.matches) aoAlargar();
  const ouvir = (e: { matches: boolean }) => { if (e.matches) aoAlargar(); };
  if (typeof midia.addEventListener === 'function') {
    midia.addEventListener('change', ouvir);
    return () => midia.removeEventListener?.('change', ouvir);
  }
  midia.addListener?.(ouvir);
  return () => midia.removeListener?.(ouvir);
}

/**
 * O "MAIS" do celular: a lista inteira numa folha que sobe de baixo. Fecha no
 * ✕, no fundo escuro e no Esc (e devolve o foco ao botão que a abriu) ou ao
 * escolher um item (aí o foco vai com a tela nova).
 */
function FolhaDoMenu({ grupos, aoFechar, aoEscolher }: {
  grupos: GrupoDoMenu[];
  aoFechar: () => void;
  aoEscolher: () => void;
}) {
  const { perfil, papel } = useSessao();
  const sair = useSair();
  const fechar = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    fechar.current?.focus();
    const tecla = (e: KeyboardEvent) => { if (e.key === 'Escape') aoFechar(); };
    window.addEventListener('keydown', tecla);
    // A página de trás não rola junto com a folha.
    const antes = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', tecla);
      document.body.style.overflow = antes;
    };
  }, [aoFechar]);

  return (
    <div className="fixed inset-0 z-50 lg:hidden print:hidden" role="dialog" aria-modal="true" aria-label="Menu do sistema">
      <div className="absolute inset-0 bg-black/60" aria-hidden="true" onClick={aoFechar} />
      <div className="absolute inset-x-0 bottom-0 flex max-h-[88svh] flex-col rounded-t-2xl border-t border-edge bg-panel pb-[env(safe-area-inset-bottom)] shadow-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-fio py-2 pl-5 pr-2">
          <p className="min-w-0 truncate text-sm">
            <span className="font-semibold">{perfil?.nome ?? '—'}</span>
            {papel && <span className="text-dim"> · {papel}</span>}
          </p>
          <button ref={fechar} type="button" onClick={aoFechar} aria-label="Fechar o menu"
                  className="grid h-11 w-11 shrink-0 place-items-center rounded-lg text-mute hover:bg-panel-alt hover:text-paper">
            <Icone d="M6 6l12 12M18 6L6 18" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-4">
          <ListaDoMenu grupos={grupos} grande aoEscolher={aoEscolher} />
        </div>
        <button type="button" onClick={() => { aoFechar(); void sair(); }}
                className="min-h-12 shrink-0 border-t border-fio px-5 text-left text-[0.9375rem] text-mute hover:bg-panel-alt hover:text-paper">
          Sair
        </button>
      </div>
    </div>
  );
}

/** A barra de baixo do celular: 4 atalhos do papel + "Mais". */
function BarraDeBaixo({ papel, maisAberto, aoAbrirMais, botaoMais }: {
  papel: Papel | null;
  maisAberto: boolean;
  aoAbrirMais: () => void;
  botaoMais: RefObject<HTMLButtonElement | null>;
}) {
  const { pathname } = useLocation();
  const atalhos = itensDaBarra(papel);
  // Fora dos atalhos (Caixa, Contas, Fiado…), quem acende é o "Mais": a pessoa
  // sempre vê ONDE está, mesmo quando a tela não tem atalho.
  const maisAtivo = maisAberto || !atalhos.some((i) => estaNoItem(pathname, i.para));
  // 3,75 rem (60 px) de propósito: a faixa do carrinho da Venda
  // (BarraCarrinho) se apoia em cima desta barra com essa mesma medida.
  const celula = 'flex h-[3.75rem] w-full flex-col items-center justify-center gap-0.5 px-1 text-[0.6875rem] font-medium transition-[color,background-color]';
  const pilula = (ativo: boolean) => `grid h-7 w-12 place-items-center rounded-full ${ativo ? 'bg-edge' : ''}`;

  return (
    <nav
      aria-label="Navegação principal"
      className="fixed inset-x-0 bottom-0 z-30 border-t border-fio bg-ink pb-[env(safe-area-inset-bottom)] lg:hidden print:hidden"
    >
      <ul className="mx-auto grid max-w-lg grid-cols-5">
        {atalhos.map((i) => (
          <li key={i.para}>
            <NavLink
              to={i.para}
              end={i.para === '/'}
              className={({ isActive }) => `${celula} ${isActive ? 'text-paper' : 'text-dim hover:text-paper'}`}
            >
              {({ isActive }) => (
                <>
                  <span className={pilula(isActive)}><Icone d={i.icone} /></span>
                  {i.rotulo}
                </>
              )}
            </NavLink>
          </li>
        ))}
        <li className={atalhos.length < ATALHOS_NA_BARRA ? 'col-start-5' : undefined}>
          <button ref={botaoMais} type="button" onClick={aoAbrirMais}
                  aria-haspopup="dialog" aria-expanded={maisAberto}
                  className={`${celula} ${maisAtivo ? 'text-paper' : 'text-dim hover:text-paper'}`}>
            <span className={pilula(maisAtivo)}><Icone d={ICONES.mais} /></span>
            Mais
          </button>
        </li>
      </ul>
    </nav>
  );
}

export default function Layout({
  titulo, acao, children,
}: { titulo: string; acao?: ReactNode; children: ReactNode }) {
  const { papel, avisoInatividade, expiraEm, adiarInatividade } = useSessao();
  const meuDinheiro = useMeuDinheiroLiberado();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const online = useConexao();
  const [ultimoSync, setUltimoSync] = useState<number | null>(null);
  const [agora, setAgora] = useState(() => Date.now());
  const [ignorarBloqueio, setIgnorarBloqueio] = useState(false);
  const [maisAberto, setMaisAberto] = useState(false);
  const botaoMais = useRef<HTMLButtonElement>(null);

  // Só pergunta a hora do dado quando está offline: com rede, o dado é de agora.
  useEffect(() => {
    if (online) return;
    let vivo = true;
    const perguntar = () => {
      void perguntarUltimoSync().then((em) => { if (vivo) setUltimoSync(em); });
    };
    perguntar();
    const id = window.setInterval(perguntar, 30_000);
    return () => { vivo = false; window.clearInterval(id); };
  }, [online]);

  // Relógio da tela: de segundo em segundo enquanto o aviso de inatividade
  // conta para trás, de meio em meio minuto só para envelhecer o "dados de X
  // min atrás". Parado quando não há nada disso — o balcão fica com a tela
  // ligada o dia inteiro.
  useEffect(() => {
    if (online && !avisoInatividade) return;
    setAgora(Date.now());
    const id = window.setInterval(() => setAgora(Date.now()), avisoInatividade ? 1_000 : 30_000);
    return () => window.clearInterval(id);
  }, [online, avisoInatividade]);

  // Voltou a internet ou mudou de tela: o bloqueio volta a valer, e o "Mais"
  // fecha (a folha aberta por cima da tela nova esconderia a própria tela).
  useEffect(() => { setIgnorarBloqueio(false); }, [pathname, online]);
  useEffect(() => { setMaisAberto(false); }, [pathname]);

  // O foco volta ao "Mais" DEPOIS de a folha sair: enquanto ela está aberta
  // o botão mora no bloco inerte, e focar ali não pega.
  const devolverFoco = useRef(false);
  const fecharMais = useCallback(() => {
    devolverFoco.current = true;
    setMaisAberto(false);
  }, []);
  const escolheuNoMais = useCallback(() => { setMaisAberto(false); }, []);
  useEffect(() => {
    if (maisAberto || !devolverFoco.current) return;
    devolverFoco.current = false;
    botaoMais.current?.focus();
  }, [maisAberto]);
  // Folha aberta e a tela alargou para o menu lateral: fecha, como no ✕.
  useEffect(() => {
    if (!maisAberto || typeof window.matchMedia !== 'function') return undefined;
    return vigiarTelaLarga(window.matchMedia(MIDIA_TELA_LARGA), fecharMais);
  }, [maisAberto, fecharMais]);

  const bloquearEscrita = !online && escreveNoBanco(pathname) && !ignorarBloqueio;

  // Quem entra no menu, por papel, é decisão de DADO — mora em gruposDoMenu,
  // logo acima, onde um teste enxerga.
  const grupos = gruposDoMenu(papel, { meuDinheiro });

  return (
    <PaginaComFolha
      aberta={maisAberto}
      pagina={(
        <div className="min-h-svh bg-ink">
          <FaixaDeAmbiente />
          <FaixaDeAtualizacao />
          <div className="lg:flex">
            <MenuLateral grupos={grupos} />

            <div className="flex min-h-svh min-w-0 flex-1 flex-col">
              {/* Os avisos ficam FIXOS no topo: o "sem conexão" não sai da tela
                  quando o vendedor rola a lista de peças. */}
              {(avisoInatividade || !online) && (
                <div className="sticky top-0 z-30 bg-ink print:hidden">
                  {/* Aviso de inatividade. Amarelo e impossível de não ver: se
                      passar despercebido, o vendedor perde o atendimento que está
                      digitando. Qualquer toque na tela já adia — o botão é para
                      quem só LÊ a tela. */}
                  {avisoInatividade && (
                    <div role="alert" className="border-b border-alerta/60 bg-alerta/12 px-4 py-2 lg:px-8">
                      <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-alerta-claro">
                          Sessão vai encerrar por inatividade em{' '}
                          <span className="numero">{contagemRegressiva(expiraEm, agora)}</span>.
                        </p>
                        <button
                          type="button"
                          onClick={adiarInatividade}
                          className="rounded-lg border border-alerta/70 px-3 py-1.5 text-xs font-semibold text-alerta-claro"
                        >
                          Continuar conectado
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Estado de conexão. Fica na tela enquanto durar a queda — o
                      vendedor tem que saber que está lendo uma cópia, e de quando
                      ela é. */}
                  {!online && (
                    <div role="status" className="border-b border-edge bg-panel-alt px-4 py-2 lg:px-8">
                      <div className="mx-auto max-w-5xl">
                        <p className="flex items-center gap-2 text-sm font-semibold text-alerta-claro">
                          <span className="ponto text-alerta" aria-hidden="true" />
                          Sem conexão
                        </p>
                        <p className="text-xs text-mute">
                          Você está vendo o que ficou guardado no aparelho — {textoDoDado(ultimoSync, agora)}.
                          O estoque pode ter mudado desde então.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* O TÍTULO DA TELA mora no conteúdo, com o mesmo tamanho e peso em
                  todas (22/600 no computador, 20/600 no celular). Ao lado, a ação
                  da tela e o sino. No celular o símbolo vem antes do título: é o
                  único lugar da marca fora do menu lateral. */}
              <header className="mx-auto flex w-full max-w-5xl items-center gap-3 px-4 pt-4 lg:px-8 lg:pt-7 print:hidden">
                <Link to="/" aria-label="Início" className="-ml-1.5 grid h-11 w-10 shrink-0 place-items-center lg:hidden">
                  <Marca className="h-5 w-auto text-marca" />
                </Link>
                <h1 className="min-w-0 flex-1 truncate text-xl font-semibold tracking-tight lg:text-[1.375rem]">
                  {titulo}
                </h1>
                <div className="flex shrink-0 items-center gap-2">
                  {acao}
                  <Sino />
                </div>
              </header>

              <main className="relative mx-auto w-full max-w-5xl flex-1 px-4 py-5 pb-28 lg:px-8 lg:pb-12 print:max-w-none print:px-0 print:py-0 print:pb-0">
                {children}

                {/* Escrita sem internet: bloqueada ANTES de digitar, não depois.
                    O conteúdo continua montado por baixo — quando a conexão voltar, o
                    que já estava preenchido continua lá. E há saída: `navigator.onLine`
                    erra (wi-fi conectado sem internet, VPN), e um falso "offline" não
                    pode parar a loja — quem insistir recebe o erro do servidor. */}
                {bloquearEscrita && (
                  <div className="absolute inset-0 z-20 flex items-start justify-center bg-ink/95 px-4 py-8 print:hidden">
                    <div className="cartao max-w-md p-6">
                      <h2 className="text-lg font-semibold text-alerta-claro">Sem conexão — não dá para registrar</h2>
                      <p className="mt-2 text-sm text-mute">
                        Esta tela grava no sistema, e agora o aparelho está sem internet. Anote no papel e
                        lance quando a conexão voltar. Buscar peça e consultar a ficha continuam funcionando.
                      </p>
                      <div className="mt-5 flex flex-wrap gap-2">
                        <button type="button" onClick={() => navigate('/')} className="botao-primario">
                          Ir para a busca
                        </button>
                        <button
                          type="button"
                          onClick={() => setIgnorarBloqueio(true)}
                          className="botao-secundario"
                        >
                          Continuar mesmo assim
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </main>
            </div>
          </div>

          <BarraDeBaixo papel={papel} maisAberto={maisAberto} botaoMais={botaoMais}
                        aoAbrirMais={() => setMaisAberto(true)} />
        </div>
      )}
      folha={<FolhaDoMenu grupos={grupos} aoFechar={fecharMais} aoEscolher={escolheuNoMais} />}
    />
  );
}

/**
 * "Tem versão nova" — a faixa que substitui o Ctrl+Shift+R.
 *
 * Aparece só quando o service worker instalou uma versão diferente da que está
 * rodando nesta aba. Um toque recarrega. NÃO recarrega sozinho: a atualização
 * chegaria no meio de uma venda, com o vendedor digitando o nome do cliente —
 * tela que pula sozinha durante o atendimento assusta, e assustar o balcão é
 * como se perde a confiança no sistema.
 *
 * Clara sobre o grafite (era o vermelho da marca até 28/10/2026): é aviso, não
 * marca, e o claro é o que mais se destaca no escuro sem virar alarme.
 */
function FaixaDeAtualizacao() {
  const [tem, setTem] = useState(false);
  useEffect(() => {
    const aviso = () => setTem(true);
    window.addEventListener(EVENTO_ATUALIZACAO, aviso);
    return () => window.removeEventListener(EVENTO_ATUALIZACAO, aviso);
  }, []);

  if (!tem) return null;
  return (
    <div className="sticky top-0 z-40 flex items-center justify-center gap-3
                    bg-paper px-3 py-1.5 text-center text-xs font-semibold text-ink print:hidden">
      <span>Tem uma versão nova do sistema.</span>
      <button type="button" onClick={() => window.location.reload()}
              className="rounded-md border border-ink/40 px-2 py-0.5 font-semibold uppercase tracking-wide">
        Atualizar
      </button>
    </div>
  );
}

/**
 * A faixa que diz, o tempo todo, que isto NÃO é a loja.
 *
 * O ambiente de desenvolvimento é uma cópia fiel da produção: mesmas peças,
 * mesmos clientes, mesmos logins e senhas. Isso é o que o torna útil para
 * testar — e é exatamente o que torna fácil cancelar a venda errada achando que
 * está brincando, ou brincar achando que está trabalhando.
 *
 * Vermelho de ERRO (não o da marca), no topo, sem botão de fechar e impressa
 * junto (`print:` não a esconde): um papel saído do dev também precisa se
 * denunciar, senão vira vale "a ver" de mentira na mão de um cliente de
 * verdade. A tinta é a do fundo na tela (contraste 7:1 sobre o vermelho claro
 * do Grafite) e a do papel na impressão.
 *
 * `VITE_AMBIENTE` é constante de COMPILAÇÃO — o build de produção não tem como
 * ligar isto por engano, e o de dev não tem como desligar.
 */
function FaixaDeAmbiente() {
  if (import.meta.env.VITE_AMBIENTE !== 'dev') return null;
  return (
    <div className="sticky top-0 z-40 bg-erro px-3 py-1 text-center text-xs font-bold uppercase tracking-widest text-ink print:text-paper">
      Ambiente de desenvolvimento — dados de brincadeira
    </div>
  );
}
